import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, ArrowRight, Calendar, Clock } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback articles if API not available
const FALLBACK_ARTICLES = [
  {
    id: '1',
    slug: 'bfrp-vs-gfrp-choosing-right-composite',
    title: 'BFRP vs GFRP: Choosing the Right Composite Rebar',
    excerpt: 'A comprehensive comparison of basalt and glass fiber reinforced polymers for different construction applications.',
    category: 'Technical Guide',
    date: '2026-02-15',
    readTime: '8 min read'
  },
  {
    id: '2',
    slug: 'marine-construction-corrosion-solution',
    title: 'Marine Construction: Solving the Corrosion Problem',
    excerpt: 'How composite reinforcement is revolutionizing coastal and marine infrastructure with 100+ year service life.',
    category: 'Industry Insight',
    date: '2026-02-10',
    readTime: '6 min read'
  },
  {
    id: '3',
    slug: 'aci-440-design-guide-engineers',
    title: 'ACI 440 Design Guide: What Engineers Need to Know',
    excerpt: 'Essential considerations for specifying FRP reinforcement under ACI 440.1R guidelines.',
    category: 'Technical Guide',
    date: '2026-02-05',
    readTime: '10 min read'
  },
  {
    id: '4',
    slug: 'lifecycle-cost-analysis-frp-steel',
    title: 'Lifecycle Cost Analysis: FRP vs Steel Reinforcement',
    excerpt: 'Breaking down the true cost of ownership when comparing composite and traditional steel rebar.',
    category: 'Business Case',
    date: '2026-01-28',
    readTime: '7 min read'
  },
];

const CATEGORIES = ['All', 'Technical Guide', 'Industry Insight', 'Business Case', 'Case Study'];

export default function ResourcesPage() {
  const [articles, setArticles] = useState(FALLBACK_ARTICLES);
  const [activeCategory, setActiveCategory] = useState('All');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/resources`);
        if (response.data.articles?.length > 0) {
          setArticles(response.data.articles);
        }
      } catch (error) {
        console.log('Using fallback articles');
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, []);

  const filteredArticles = activeCategory === 'All' 
    ? articles 
    : articles.filter(a => a.category === activeCategory);

  return (
    <div className="bg-charcoal min-h-screen pt-20" data-testid="resources-page">
      {/* Hero */}
      <section className="py-16 lg:py-24 border-b border-border-dark">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center gap-4 mb-4">
              <BookOpen size={24} className="text-gold" />
              <span className="tech-label">//KNOWLEDGE_BASE</span>
            </div>
            <h1 className="heading-display text-stone text-5xl md:text-6xl mb-6">
              Resources & <span className="text-gold">Insights</span>
            </h1>
            <p className="text-stone/50 max-w-2xl text-lg">
              Technical guides, industry insights, and best practices for specifying 
              composite reinforcement materials.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 border-b border-border-dark">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-wrap gap-4">
            {CATEGORIES.map(category => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-4 py-2 text-mono text-xs transition-all ${
                  activeCategory === category
                    ? 'bg-gold text-charcoal'
                    : 'border border-border-dark text-stone/60 hover:border-gold/30'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-2 border-gold border-t-transparent animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredArticles.map((article, index) => (
                <motion.article
                  key={article.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="group"
                >
                  <Link 
                    to={`/resources/${article.slug}`}
                    className="block h-full p-6 border border-border-dark hover:border-gold/30 transition-all bg-surface-dark/30"
                    data-testid={`article-${article.id}`}
                  >
                    <span className="text-mono text-xs text-gold/70">{article.category}</span>
                    <h2 className="heading-section text-stone text-xl mt-2 mb-4 group-hover:text-gold transition-colors">
                      {article.title}
                    </h2>
                    <p className="text-stone/50 text-sm mb-6 line-clamp-3">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-stone/30 text-xs">
                      <div className="flex items-center gap-2">
                        <Calendar size={12} />
                        <span>{new Date(article.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock size={12} />
                        <span>{article.readTime}</span>
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-16 lg:py-24 border-t border-border-dark bg-surface-dark/30">
        <div className="max-w-2xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="heading-display text-stone text-3xl mb-4">Stay Updated</h2>
          <p className="text-stone/50 mb-8">
            Get the latest technical guides and industry insights delivered to your inbox.
          </p>
          <Link to="/contact" className="btn btn-primary">
            Subscribe to Updates
            <ArrowRight size={16} className="ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
}
